import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { extractApiError } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import {
  useInitiateKaspi,
  usePayment,
  usePayments,
  useUploadPaymentProof,
} from '@/hooks/usePayments';
import { formatCurrency, formatDate, formatPeriod, isDesktop } from '@/utils/format';

export function PaymentDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const paymentId = id ? Number(id) : null;
  const navigate = useNavigate();
  const paymentQuery = usePayment(paymentId);
  // Платежи того же договора -- для ручного выбора месяца (бэкенд auto-select мог
  // указать не на тот платёж). lease_id известен только после загрузки платежа.
  const leaseId = paymentQuery.data?.lease_id;
  const monthsQuery = usePayments(leaseId ? { lease_id: leaseId } : {});
  const initiateKaspi = useInitiateKaspi();
  const uploadProof = useUploadPaymentProof(paymentId ?? 0);
  const fileInput = useRef<HTMLInputElement>(null);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!paymentId) return null;
  if (paymentQuery.isLoading) return <Spinner />;
  if (!paymentQuery.data) return null;

  const payment = paymentQuery.data;
  const desktop = isDesktop();
  // Чек загружен, но платёж ещё не подтверждён владельцем -- иначе арендатор
  // видит «Ожидает» и думает, что загрузка не сработала.
  const proofPending =
    payment.status !== 'paid' &&
    payment.status !== 'cancelled' &&
    payment.payment_proofs.length > 0;

  // Месяцы того же договора для ручного выбора (без отменённых), по возрастанию.
  const monthOptions = [...(monthsQuery.data?.data ?? [])]
    .filter((p) => p.status !== 'cancelled')
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  const payWithKaspi = async () => {
    const res = await initiateKaspi.mutateAsync(payment.id);
    setQrImage(res.qr_image);
    setDeepLink(res.deep_link);
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    const form = new FormData();
    // Бэкенд ждёт поле `proof` (НЕ `file`) -- иначе 422 и загрузка молча падала.
    form.append('proof', file);
    try {
      await uploadProof.mutateAsync(form);
    } catch (err) {
      setUploadError(extractApiError(err).message);
    } finally {
      // сбросить, чтобы можно было выбрать тот же файл повторно
      e.target.value = '';
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {monthOptions.length > 1 ? (
        <label className="block text-sm font-medium text-slate-700 md:col-span-2">
          {t('payments.select_period')}
          <select
            value={payment.id}
            onChange={(e) => navigate(`/payments/${e.target.value}`)}
            className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {monthOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {formatPeriod(t, p.period_month, p.period_year)} — {t(`payments.status.${p.status}`)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <Card title={formatPeriod(t, payment.period_month, payment.period_year)}>
        <p className="text-2xl font-semibold">{formatCurrency(payment.amount_expected)}</p>
        <p className="text-xs text-slate-500">
          {t('payments.due_by')}: {formatDate(payment.due_date)}
        </p>
        <div className="mt-2">
          <Badge tone={payment.status === 'paid' ? 'success' : 'warning'}>
            {t(`payments.status.${payment.status}`)}
          </Badge>
        </div>
        {proofPending ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t('payments.proof_review')}
          </p>
        ) : null}
      </Card>

      <Card title={t('payments.pay_kaspi')}>
        {qrImage ? (
          <>
            <img src={qrImage} alt="Kaspi QR" className="mx-auto h-48 w-48" />
            {desktop ? (
              <p className="mt-3 text-center text-xs text-slate-500">
                {t('web.desktop.scan_qr_with_kaspi')}
              </p>
            ) : deepLink ? (
              <a
                href={deepLink}
                className="mt-3 block rounded-lg bg-brand px-4 py-2 text-center text-sm font-medium text-white"
              >
                {t('payments.open_kaspi')}
              </a>
            ) : null}
          </>
        ) : (
          <Button onClick={() => void payWithKaspi()} loading={initiateKaspi.isPending}>
            {t('payments.pay_kaspi')}
          </Button>
        )}
      </Card>

      <Card title={t('payments.upload_proof')} className="md:col-span-2">
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          onChange={onFileSelected}
          className="hidden"
        />
        <Button
          variant="secondary"
          onClick={() => fileInput.current?.click()}
          loading={uploadProof.isPending}
        >
          {t('payments.upload_proof')}
        </Button>
        {uploadError ? <p className="mt-2 text-sm text-red-600">{uploadError}</p> : null}
        {payment.payment_proofs.length > 0 && (
          <ul className="mt-4 space-y-1 text-sm">
            {payment.payment_proofs.map((p, i) =>
              p.url ? (
                <li key={i}>
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                    {t('payments.proof')} #{i + 1} — {formatDate(p.uploaded_at)}
                  </a>
                </li>
              ) : (
                <li key={i} className="text-slate-400">
                  {t('payments.proof')} #{i + 1} — {formatDate(p.uploaded_at)}
                </li>
              ),
            )}
          </ul>
        )}
      </Card>
    </div>
  );
}
